import { useState, useEffect, useMemo } from 'react';
import { Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getScorebugLogoUrl, getThumbnailUrl } from '@/lib/cloudinary';
import type { Team } from '@shared/schema';

type TeamLogoSize = 'xs' | 'sm' | 'sm-mobile' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<TeamLogoSize, string> = {
  xs: 'w-6 h-6 aspect-square',
  sm: 'w-8 h-8 aspect-square',
  'sm-mobile': 'w-10 h-10 aspect-square',
  md: 'w-12 h-12 aspect-square',
  lg: 'w-16 h-16 aspect-square',
  xl: 'w-20 h-20 aspect-square',
};

const SIZE_PX: Record<TeamLogoSize, number> = {
  xs: 24,
  sm: 32,
  'sm-mobile': 40,
  md: 48,
  lg: 64,
  xl: 80,
};

const ICON_SIZES: Record<TeamLogoSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  'sm-mobile': 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-9 h-9',
};

/** Thumbnail fetch dimensions per display size (Cloudinary f_auto,q_auto,dpr_auto). */
const THUMBNAIL_PX: Record<TeamLogoSize, number> = {
  xs: 32,
  sm: 32,
  'sm-mobile': 48,
  md: 128,
  lg: 128,
  xl: 200,
};

/** Scorebug circular treatment pixel size for broadcast surfaces. */
const SCOREBUG_PX: Record<TeamLogoSize, number> = {
  xs: 48,
  sm: 56,
  'sm-mobile': 64,
  md: 80,
  lg: 100,
  xl: 120,
};

interface TeamLogoProps {
  team?: Team | null;
  size?: TeamLogoSize;
  /** `thumbnail` = card/table avatars; `scorebug` = circular broadcast treatment for live boards. */
  variant?: 'thumbnail' | 'scorebug';
}

export function TeamLogo({ team, size = 'sm', variant = 'thumbnail' }: TeamLogoProps) {
  const [imageError, setImageError] = useState(false);
  const logoUrl = (team as any)?.logoUrl as string | undefined;

  const displayUrl = useMemo(() => {
    if (!logoUrl) return '';
    if (variant === 'scorebug') {
      return getScorebugLogoUrl(logoUrl, SCOREBUG_PX[size]);
    }
    const px = THUMBNAIL_PX[size];
    return getThumbnailUrl(logoUrl, px, px);
  }, [logoUrl, size, variant]);

  useEffect(() => {
    setImageError(false);
  }, [team?.id, logoUrl, displayUrl]);

  const sizeClasses = SIZE_CLASSES[size];
  const iconSizes = ICON_SIZES[size];

  if (!team) {
    return (
      <div
        className={cn(
          'rounded-full bg-pro-field/20 flex items-center justify-center flex-shrink-0 border border-pro-field/30',
          sizeClasses
        )}
      >
        <Shield className={cn('text-pro-field', iconSizes)} />
      </div>
    );
  }

  const hasValidLogo = Boolean(displayUrl) && !imageError;

  if (hasValidLogo) {
    return (
      <div
        className={cn(
          'rounded-full flex items-center justify-center flex-shrink-0 border border-border overflow-hidden bg-muted',
          sizeClasses
        )}
        title={team.name}
      >
        <img
          src={displayUrl}
          alt={`${team.name} logo`}
          width={SIZE_PX[size]}
          height={SIZE_PX[size]}
          decoding="async"
          className="w-full h-full object-cover aspect-square"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center flex-shrink-0 border border-pro-field/30 bg-pro-field/20',
        sizeClasses
      )}
      title={team.name}
    >
      <Shield className={cn('text-pro-field', iconSizes)} />
    </div>
  );
}
